import { Autocomplete, Checkbox, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from "@mui/material";
import { AutoCompletes, DateInput, TextFields } from "../Custom/custominput";
import { format } from 'date-fns';
import { UploadButton } from "../Custom/Button";
import axiosWithToken from "./axiosTokenConfig";
import { toast } from "react-toastify";
import { BASE_URL, DOMAIN } from "../../global";
import { useEffect, useState } from "react";
import Text from "../Custom/text";
import { clienTypes } from "./common";
import MultiValueInputModal from "./multiValueInputModal";
import { fetchByTable, fetchQuery } from "./fetched";

const CommonForm = ({ inputStates, updateInputObject, inputFields, colSpan, size, }) => {
  const [fetchedOptions, setFetchedOptions] = useState({});
  const [inputErrors, setInputErrors] = useState({});
  const [clientAddresses, setClientAddresses] = useState([]);

  useEffect(() => {
    const fetchOptions = async (tableName, optionsFilter) => {
      try {
        const response = await fetchQuery(
          `select * from ${tableName} ${optionsFilter} `
        );
        const data =
          tableName === "clients"
            ? response.map(c => ({
              ...c,
              companyName: c.companyName + (c.uniCode ? ` (${c.uniCode})` : "") + (c.type ? ` (${clienTypes.find(ct => ct.value == c.type)?.short || ""})` : "")
            }))
            : response;
        setFetchedOptions((prevOptions) => ({
          ...prevOptions,
          [tableName]: data,
        }));
      } catch (error) {
        toast.error("Something Went Wrong!");
      }
    };

    inputFields.forEach(({ type, options, optionsFilter }) => {
      if (
        type === "Autocomplete" &&
        options &&
        !Array.isArray(options) &&
        !fetchedOptions[options]
      ) {
        fetchOptions(options, optionsFilter ? optionsFilter : "");
      }
    });
  }, [inputFields]);

  // Fetch addresses when client changes
  useEffect(() => {
    const fetchClientAddresses = async () => {
      if (inputStates?.clientId) {
        try {
          // Find selected client
          const clientOptions = fetchedOptions["clients"] || [];
          const selectedClient = clientOptions.find(
            (client) => client.id === inputStates.clientId
          );

          if (selectedClient && selectedClient.address) {
            // Parse address IDs (comma-separated)
            const addressIds = selectedClient.address
              .split(",")
              .map((id) => id.trim());
            // Fetch all addresses
            const allAddresses = await fetchQuery(`SELECT * FROM addresses `);
            // Filter addresses based on client's address IDs
            const filteredAddresses = allAddresses.filter((address) =>
              addressIds.includes(address.id.toString())
            );
            setClientAddresses(filteredAddresses);

            // Auto-select if only one address
            if (filteredAddresses.length === 1) {
              updateInputObject("addressId", filteredAddresses[0].id);
            } else if (filteredAddresses.length === 0) {
              updateInputObject("addressId", "");
            }
          } else {
            setClientAddresses([]);
            updateInputObject("addressId", "");
          }
        } catch (error) {
          console.error("Error fetching client addresses:", error);
          setClientAddresses([]);
          updateInputObject("addressId", "");
        }
      } else {
        setClientAddresses([]);
        // Clear address selection when client is cleared
        if (inputStates.addressId) {
          updateInputObject("addressId", "");
        }
      }
    };

    fetchClientAddresses();
  }, [inputStates.clientId, fetchedOptions]);

  const handleFileUpload12 = (event, stateKey) => {
    const file = event.target.files[0];
    // added validation only
    if (file && /[&#$*]/.test(file.name)) {
      toast.warning("File name contains special characters. Please rename the file and upload again.");
      return;
    }
    const data = new FormData();
    data.append("fileToUpload", file);
    axiosWithToken
      .post(BASE_URL + `fileUpload/fileUpload.php`, data)
      .then((res) => {
        if (res.status == 200) {
          updateInputObject(stateKey, res.data);
          toast.success("File Uploaded Successfully!");
        }
      })
      .catch(() => toast.error("Something went wrong"));
  };

  const renderInputField = (fieldConfig, index) => {
    const {
      type,
      label,
      tooltipLabel,
      downloadFile,
      stateKey,
      valueKey,
      options,
      getOptionLabelKey,
      disabled = false,
      disablePastDates = false,
      minimumDate,
      disableFutureDates,
    } = fieldConfig;

    const selectedValue =
      inputStates[stateKey] !== undefined && inputStates[stateKey] !== null
        ? inputStates[stateKey]
        : "";

    switch (type) {
      case "Autocomplete":
        const fullOptions = Array.isArray(options) ? options : [];
        const currentValue =
          fullOptions.find((item) => item[valueKey] === selectedValue) || null;

        return (
          <div key={index} className="w-full">
            <Autocomplete
              {...(size === "small" && { className: "Listinputcss" })}
              size="small"
              id={`autocomplete-${stateKey}-${index}`}
              options={
                Array.isArray(options) ? options : fetchedOptions[options] || []
              }
              getOptionLabel={(option) =>
                options === "employees"
                  ? `${option["employeesCode"] || ""} - ${option[getOptionLabelKey] || ""
                  }`
                  : option[getOptionLabelKey] || ""
              }
              value={
                (Array.isArray(options)
                  ? options
                  : fetchedOptions[options] || []
                ).find((item) => item[valueKey] == selectedValue) || null
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth label={label} />
              )}
              onChange={(event, value) => {
                updateInputObject(stateKey, value ? value[valueKey] : "");
              }}
              disabled={disabled}
            />
          </div>
        );

      case "ClientAddress":
        // If only one address available, show as disabled text field
        if (clientAddresses.length === 1) {
          const singleAddress = clientAddresses[0];
          return (
            <div key={index} className="w-full">
              <TextFields
                {...(size === "small" && {
                  className: "Listinputcss",
                  size: "small",
                })}
                id={`client-address-text-${stateKey}-${index}`}
                label={label}
                value={singleAddress[getOptionLabelKey] || ""}
                disabled={true}
              />
            </div>
          );
        }

        // If multiple addresses or none, show autocomplete dropdown
        return (
          <div key={index} className="w-full">
            <Autocomplete
              {...(size === "small" && { className: "Listinputcss" })}
              size="small"
              id={`client-address-${stateKey}-${index}`}
              options={clientAddresses}
              getOptionLabel={(option) => option[getOptionLabelKey] || ""}
              value={
                clientAddresses.find(
                  (item) => item[valueKey] == selectedValue
                ) || null
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth label={label} />
              )}
              onChange={(event, value) => {
                updateInputObject(stateKey, value ? value[valueKey] : "");
              }}
              disabled={
                disabled ||
                !inputStates.clientId ||
                clientAddresses.length === 0
              }
            />
          </div>
        );
      case "TextField":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`text-field-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => {
                const val = e.target.value;
                if (fieldConfig.inputType === "number") {
                  // Accept only digits
                  if (/^\d*\.?\d*$/.test(val)) {
                    updateInputObject(stateKey, val);
                  }
                } else {
                  updateInputObject(stateKey, val);
                }
              }}
              disabled={disabled}
            />
          </div>
        );

      case "MultiSelect": {
        const multiOptions = Array.isArray(options) ? options : [];
        const selectedValues = Array.isArray(selectedValue) ? selectedValue : [];

        const handleToggle = (val) => {
          let updated;
          if (selectedValues.includes(val)) {
            // remove if already selected (toggle off)
            updated = selectedValues.filter((v) => v !== val);
          } else {
            // add if not selected (mark true / select)
            updated = [...selectedValues, val];
          }
          updateInputObject(stateKey, updated);
        };

        return (
          <div key={index} className="w-full col-span-full">
            {label && (
              <label className="block font-bold  text-sm font-medium text-gray-700 mb-1">
                {label}
              </label>
            )}
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {multiOptions.map((option) => {
                const isSelected = selectedValues.includes(option[valueKey]);
                return (
                  <button
                    type="button"
                    key={option[valueKey]}
                    disabled={disabled}
                    onClick={() => handleToggle(option[valueKey])}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md border text-xs sm:text-sm font-medium transition-colors
                ${isSelected
                        ? "border-blue-500 bg-blue-50 text-gray-800"
                        : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }
                disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <span
                      className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border
                  ${isSelected ? "border-blue-500 bg-blue-500" : "border-gray-400"}`}
                    >
                      {isSelected && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    {option[getOptionLabelKey]}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      case "Heading":
        return (
          <div key={index} className="col-span-full">
            <Text variant="h6" bold className="text-blue-700 bg-blue-50 px-3">
              {label}
            </Text>
          </div>
        );

      case "MultipleAutocomplete":
        const multiOptions = Array.isArray(options)
          ? options
          : fetchedOptions?.[options] || [];

        const selectedValueIds = (inputStates?.[stateKey] || "")
          ?.split(",") // from string "1,2" → array ["1", "2"]
          ?.map((id) => id.trim())
          ?.filter(Boolean);

        const selectedValues = multiOptions.filter((item) =>
          selectedValueIds?.includes(String(item[valueKey]))
        );

        return (
          <div key={index} className="w-full">
            <Autocomplete
              {...(size === "small" && { className: "Listinputcss" })}
              multiple
              size="small"
              limitTags={1}
              id={`multi-autocomplete-${stateKey}-${index}`}
              options={multiOptions}
              getOptionLabel={(option) =>
                option[getOptionLabelKey] || option[valueKey] || ""
              }
              value={selectedValues}
              onChange={(event, newValue) => {
                const selectedIds = newValue.map((item) => item[valueKey]);
                // Convert array to comma-separated string like "1,2"
                updateInputObject(stateKey, selectedIds.join(","));
              }}
              renderInput={(params) => (
                <TextField {...params} fullWidth label={label} />
              )}
              disabled={disabled}
            />
          </div>
        );

      case "Table": {
        const columns = Array.isArray(options) ? options : [];
        const rows = Array.isArray(selectedValue) ? selectedValue : [];

        const MIN_ROWS = 1;
        const MAX_ROWS = 12;
        const LINE_HEIGHT = 20; // px per line for text-sm + leading

        const autoResize = (el) => {
          if (!el) return;
          el.style.height = "auto";
          const maxHeight = LINE_HEIGHT * MAX_ROWS;
          const minHeight = LINE_HEIGHT * MIN_ROWS + 8; // + vertical padding
          const nextHeight = Math.min(el.scrollHeight, maxHeight);
          el.style.height = `${Math.max(nextHeight, minHeight)}px`;
          el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
        };

        const handleAddRow = () => {
          const newRow = {};
          columns.forEach((col) => {
            newRow[col] = "";
          });
          updateInputObject(stateKey, [...rows, newRow]);
        };

        const handleRemoveRow = (rowIndex) => {
          const updated = rows.filter((_, i) => i !== rowIndex);
          updateInputObject(stateKey, updated);
        };

        const handleCellChange = (rowIndex, col, val) => {
          const updated = rows.map((row, i) =>
            i === rowIndex ? { ...row, [col]: val } : row
          );
          updateInputObject(stateKey, updated);
        };

        return (
          <div key={index} className="w-full col-span-full">
            {/* Label left, Add Row button right */}
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">
                {label || ""}
              </label>
              <button
                type="button"
                disabled={disabled}
                onClick={handleAddRow}
                className="px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md border border-blue-500 text-blue-600 bg-white hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + Add Row
              </button>
            </div>

            {/* Table */}
            <div className="w-full overflow-x-auto border border-gray-200 rounded-md">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    {columns.map((col, colIdx) => (
                      <th
                        key={col}
                        className={`px-3 py-2 text-center font-medium text-gray-700 border-b border-gray-200 whitespace-nowrap
                    ${colIdx !== 0 ? "border-l border-gray-200" : ""}`}
                      >
                        {col}
                      </th>
                    ))}
                    <th className="px-3 py-2 border-b border-l border-gray-200 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-3 py-4 text-center text-gray-400 text-sm"
                      >
                        No rows added yet
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-b border-gray-100 last:border-b-0">
                        {columns.map((col, colIdx) => (
                          <td
                            key={col}
                            className={`px-2 py-1.5 align-top ${colIdx !== 0 ? "border-l border-gray-200" : ""}`}
                          >
                            <textarea
                              value={row[col] || ""}
                              disabled={disabled}
                              rows={MIN_ROWS}
                              ref={(el) => autoResize(el)}
                              onChange={(e) => {
                                handleCellChange(rowIndex, col, e.target.value);
                                autoResize(e.target);
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed text-center resize-none leading-tight"
                            />
                          </td>
                        ))}
                        <td className="px-2 py-1.5 text-center border-l border-gray-200 align-top">
                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() => handleRemoveRow(rowIndex)}
                            className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove row"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case "Textarea":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`textarea-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => updateInputObject(stateKey, e.target.value)}
              disabled={disabled}
              multiline
              minRows={1}
              maxRows={10}
            />
          </div>
        );

      case "Textarea1":
        return (
          <div key={index} className="w-full col-span-2">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`textarea-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => updateInputObject(stateKey, e.target.value)}
              disabled={disabled}
              multiline
              minRows={2}
              maxRows={10}
            />
          </div>
        );

      case "DatePicker":
        const today = new Date();

        const maxDateOfBirth =
          stateKey === "dateOfBirth"
            ? new Date(today.setFullYear(today.getFullYear() - 18))
            : disableFutureDates
              ? new Date()
              : undefined;

        const defaultValue =
          stateKey === "applyLeaveDate"
            ? new Date()
            : selectedValue
              ? new Date(selectedValue)
              : "";

        return (
          <div key={index} className="w-full">
            <DateInput
              id="from-date"
              label={label}
              value={defaultValue}
              onChange={(date) => {
                const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
                updateInputObject(stateKey, formattedDate);
              }}
              disabled={disabled}
              maxDate={maxDateOfBirth}
              minDate={
                disablePastDates
                  ? minimumDate
                    ? new Date(minimumDate)
                    : today
                  : undefined
              }
            />
          </div>
        );

      case "Checkbox":
        return (
          <div key={index} className="w-full">
            <FormControlLabel
              control={
                <Checkbox
                  id={`checkbox-${stateKey}-${index}`}
                  checked={Boolean(selectedValue)}
                  onChange={(e) =>
                    updateInputObject(stateKey, e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              label={label}
            />
          </div>
        );

      case "ImageUpload":
        return (
          <div key={index} className="w-full">
            <UploadButton
              label={label}
              tooltipLabel={tooltipLabel}
              downloadFile={downloadFile}
              onChange={(e) => handleFileUpload12(e, stateKey)}
              outline={false}
              fileUrl={selectedValue}
              showFile={true}
              onRemove={() => updateInputObject(stateKey, null)}
            />
          </div>
        );

      case "TextFieldClientIntials":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`text-field-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => {
                const val = e.target.value
                  .toUpperCase() // Ensure it's uppercase
                  .replace(/[^A-Z]/g, ""); // Remove anything that isn't a letter
                updateInputObject(stateKey, val);
              }}
              disabled={disabled}
            />
          </div>
        );

      case "emailId":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`email-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => {
                const val = e.target.value;
                updateInputObject(stateKey, val);
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const errorMessage =
                  val && !emailRegex.test(val) ? "Invalid email format" : "";
                setInputErrors((prev) => ({
                  ...prev,
                  [stateKey]: errorMessage,
                }));
              }}
              error={Boolean(inputErrors[stateKey])}
              helperText={inputErrors[stateKey] || ""}
              disabled={disabled}
            />
          </div>
        );

      case "phoneNo":
        return (
          <div key={index} className="w-full">
            <TextFields
              {...(size === "small" && {
                className: "Listinputcss",
                size: "small",
              })}
              id={`phone-${stateKey}-${index}`}
              label={label}
              value={selectedValue}
              onChange={(e) => {
                const onlyDigits = e.target.value.replace(/\D/g, "");
                updateInputObject(stateKey, onlyDigits);
                const errorMessage =
                  onlyDigits && onlyDigits.length !== 10
                    ? "Phone number must be exactly 10 digits"
                    : "";
                setInputErrors((prev) => ({
                  ...prev,
                  [stateKey]: errorMessage,
                }));
              }}
              error={Boolean(inputErrors[stateKey])}
              helperText={inputErrors[stateKey] || ""}
              disabled={disabled}
            />
          </div>
        );

      case "UnitModal":
        return (
          <div key={index} className="w-full">
            <MultiValueInputModal
              buttonLabel={label}
              stateKey={stateKey}
              inputStates={inputStates}
              updateInputObject={updateInputObject}
              groupId={fieldConfig.groupId}
            />
          </div>
        );

      case "emptySpace":
        return <div key={index} className="w-full" />;

      default:
        return null; // Handle other types if needed
    }
  };

  return (
    <div
      className={`grid gap-4 ${colSpan?.length
        ? `grid-cols-${colSpan[0]} sm:grid-cols-${colSpan[1]} md:grid-cols-${colSpan[2]} lg:grid-cols-${colSpan[3]}`
        : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        }`}
    >
      {inputFields.map(renderInputField)}
    </div>
  );
};

export default CommonForm;
